import { useCallback, useEffect, useRef, useState } from 'react'
import type { Order, DiningTable } from '../lib/db'
import { TableCard } from './TableCard'

/**
 * Sơ đồ bàn kéo thả. Chủ quán xếp các ô cho giống mặt bằng thật của quán —
 * nhìn vào màn hình phải nhận ra ngay bàn nào ở góc nào, không phải đọc tên bàn rồi đoán.
 *
 * Lưới cố định số cột; ô bàn bám vào ô lưới. Kéo bằng Pointer Events nên chuột trên
 * máy tính và ngón tay trên tablet đi chung một đường code.
 */

export const GRID_COLS = 6
const MIN_ROWS = 4
const GAP = 10

export interface Placement {
  gridX: number
  gridY: number
  gridW: number
  gridH: number
}

interface Props {
  tables: DiningTable[]
  openOrders: Map<string, Order>
  now: number
  editing: boolean
  selectedId?: string | null
  onPick: (table: DiningTable) => void
  onMove: (id: string, next: Placement) => void
}

interface DragState {
  id: string
  /** Khoảng lệch giữa điểm chạm và góc trên trái của ô — giữ để ô không nhảy dưới ngón tay. */
  offsetX: number
  offsetY: number
  gridX: number
  gridY: number
}

export function FloorPlan({ tables, openOrders, now, editing, selectedId, onPick, onMove }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [cell, setCell] = useState(96)
  const [drag, setDrag] = useState<DragState | null>(null)

  const rows = Math.max(MIN_ROWS, ...tables.map((t) => t.gridY + t.gridH))

  // Ô lưới co theo bề ngang thật của khung, để cùng một sơ đồ dùng được
  // trên điện thoại dọc lẫn màn hình quầy nằm ngang.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const width = el.clientWidth
      setCell(Math.max(64, (width - GAP * (GRID_COLS - 1)) / GRID_COLS))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const posOf = useCallback(
    (t: DiningTable) => {
      const isDragging = drag?.id === t.id
      const gx = isDragging ? drag.gridX : t.gridX
      const gy = isDragging ? drag.gridY : t.gridY
      return {
        left: gx * (cell + GAP),
        top: gy * (cell + GAP),
        width: t.gridW * cell + (t.gridW - 1) * GAP,
        height: t.gridH * cell + (t.gridH - 1) * GAP,
      }
    },
    [cell, drag],
  )

  const clampToGrid = useCallback(
    (px: number, py: number, w: number, h: number) => ({
      gridX: Math.max(0, Math.min(GRID_COLS - w, Math.round(px / (cell + GAP)))),
      gridY: Math.max(0, Math.round(py / (cell + GAP))),
      gridW: w,
      gridH: h,
    }),
    [cell],
  )

  function handlePointerDown(e: React.PointerEvent, t: DiningTable) {
    if (!editing) return
    const host = ref.current
    if (!host) return

    const box = host.getBoundingClientRect()
    const p = posOf(t)

    e.currentTarget.setPointerCapture(e.pointerId)
    setDrag({
      id: t.id,
      offsetX: e.clientX - box.left - p.left,
      offsetY: e.clientY - box.top - p.top,
      gridX: t.gridX,
      gridY: t.gridY,
    })
  }

  function handlePointerMove(e: React.PointerEvent, t: DiningTable) {
    if (!drag || drag.id !== t.id) return
    const host = ref.current
    if (!host) return

    const box = host.getBoundingClientRect()
    const px = e.clientX - box.left - drag.offsetX
    const py = e.clientY - box.top - drag.offsetY
    const snapped = clampToGrid(px, py, t.gridW, t.gridH)

    if (snapped.gridX !== drag.gridX || snapped.gridY !== drag.gridY)
      setDrag({ ...drag, gridX: snapped.gridX, gridY: snapped.gridY })
  }

  function handlePointerUp(t: DiningTable) {
    if (!drag || drag.id !== t.id) return
    const moved = drag.gridX !== t.gridX || drag.gridY !== t.gridY
    if (moved) onMove(t.id, { gridX: drag.gridX, gridY: drag.gridY, gridW: t.gridW, gridH: t.gridH })
    setDrag(null)
    // Kéo xong thì không mở bàn — nhấc tay ra là chốt vị trí, thế thôi.
    if (!moved) onPick(t)
  }

  /** Bàn phím cũng dời được ô: mũi tên đi từng ô lưới, cho người dùng máy tính để bàn. */
  function handleKeyDown(e: React.KeyboardEvent, t: DiningTable) {
    if (!editing) return
    const step: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    }
    const d = step[e.key]
    if (!d) return
    e.preventDefault()
    onMove(t.id, {
      gridX: Math.max(0, Math.min(GRID_COLS - t.gridW, t.gridX + d[0])),
      gridY: Math.max(0, t.gridY + d[1]),
      gridW: t.gridW,
      gridH: t.gridH,
    })
  }

  return (
    <div
      ref={ref}
      className={`floor${editing ? ' is-editing' : ''}`}
      style={{
        height: rows * cell + (rows - 1) * GAP,
        backgroundSize: `${cell + GAP}px ${cell + GAP}px`,
      }}
    >
      {tables.map((t) => {
        const p = posOf(t)
        const dragging = drag?.id === t.id
        return (
          <div
            key={t.id}
            className={`floor-slot${dragging ? ' is-dragging' : ''}`}
            style={{ transform: `translate(${p.left}px, ${p.top}px)`, width: p.width, height: p.height }}
            onPointerDown={(e) => handlePointerDown(e, t)}
            onPointerMove={(e) => handlePointerMove(e, t)}
            onPointerUp={() => handlePointerUp(t)}
            onPointerCancel={() => setDrag(null)}
            onKeyDown={(e) => handleKeyDown(e, t)}
          >
            <TableCard
              table={t}
              order={openOrders.get(t.id)}
              now={now}
              editing={editing}
              selected={selectedId === t.id}
              onClick={editing ? undefined : () => onPick(t)}
            />
          </div>
        )
      })}
    </div>
  )
}
